<template>
    <div data-testid="cycle-report" class="all-height all-width">
        <div class="pa-6 pb-16 d-flex flex-column gap-6">
            
            <ReportHeader title="All Unit Cycle Report" 
            test-id-prefix="cycle-report" 
            @refresh="fetchInitialData" />

            <div class="d-flex flex-column gap-6">
                <ReportFilter 
                apply-button-text="Generate" 
                cycletype="Actual" 
                :showrole="false" 
                :showyear="true"
                :showperiod="true" 
                :shownetwork="true" 
                :showregion="true" 
                :showcountry="true"
                :showreportingunit="true" 
                :showSync="false" 
                :showFavourite="false" 
                :showReset="true"
                :showApply="true" 
                :showrutype="true" 
                :showmultireportingunit="true" 
                :isLoading="filterLoading"
                :showmultinetwork="true" 
                :showmulticountry="true" 
                :showmultiregion="true"
                @newdata="handleFilterData" />
            </div>

            <ReportTable 
            :loading="getCycleReportListLoading" 
            :headers="getCycleReportHeader" 
            :items="formatedData"
            :total-pages="getCycleReportTotalPages" 
            :current-page="currentPage" 
            :items-per-page="itemsPerPage"
            test-id-prefix="cycle-report" 
            :dataSlots="['indexno','period_detail', 'created_at', 'status', 'action']"
            @page-change="handlePageChange" 
            @download="downloadFile"
            @view-details="showDetails"
            @filter-change="handleTableFilterChange">
                <template v-slot:indexno="{ item }">
                    <div>
                        {{ item.serial_number }}
                    </div>
                </template>
                <template v-slot:period_detail="{ item }">
                    <div>
                        {{ item.period_detail ? item.period_detail.year : '' }} - {{ getMonthName(item.period_detail ? item.period_detail.number : '') }}
                    </div>
                </template>
            </ReportTable>
        </div>

        <ReportDialog v-model="detailsDialog" 
        v-if="detailsDialog && detailsItem" 
        title="All Unit Cycle Report Details"
        :details-item="detailsItem" 
        test-id-prefix="cycle-report-dialog">
            <template v-slot:header-fields="{ item }">
                <v-row no-gutters class="mb-2 align-center">
                    <v-col cols="12" sm="4" class="details-label">Generated Time</v-col>
                    <v-col cols="12" sm="8" class="details-value font-weight-semibold">
                        {{ item.period_detail ? item.period_detail.year : '' }} - {{ getMonthName(item.period_detail ? item.period_detail.number : '') }}
                    </v-col>
                </v-row>
            </template>
        </ReportDialog>

    </div>
</template>
<script>
import ReportHeader from './components/ReportHeader.vue';
import ReportTable from './components/ReportTable.vue';
import ReportDialog from './components/ReportDialog.vue';
import { STATUS_VARIANTS } from '@/modules/varianceanalysis/utils/statusConstants.js';
import { PAGINATION, REPORTS_API, MONTHS } from '@/modules/varianceanalysis/utils/constants.js';
import { mapActions, mapGetters } from 'vuex';
import ReportFilter from '../../views/ReportFilter.vue';

export default {
    name: 'CycleReport',

    components: {
        ReportFilter,
        ReportHeader,
        ReportTable,
        ReportDialog
    },

    data() {
        return {
            selectedFilter: {},
            cycletype: 'Actual',
            filterLoading: false,
            filterList: {},
            detailsDialog: false,
            detailsItem: null,
            fetchTimeout: null,
            isInitialLoad: true, // Track if this is the first load
            currentPage: 1,
            itemsPerPage: PAGINATION.DEFAULT_LIMIT,
            statusFilter: [],
        };
    },

    mounted() {
        // Initial data fetch is deferred to handleFilterData when ReportFilter
        // signals loading complete (key='loading', value=false).
        // This prevents duplicate API calls from DataTableNew's created() hook
        // and mounted() both triggering fetches independently.
    },

    computed: {
        ...mapGetters('varianceanalysis/reports', [
            'getCycleReportData',
            'getCycleReportListLoading',
            'getCycleReportFilterLoading',
            'getCycleReportHeader',
            'getCycleReportTotalPages',
        ]),

        /**
         * Generates shimmer/skeleton placeholder items for loading state
         * @returns {Array} Array of placeholder items for skeleton loader display
         */
        shimmerItems() {
            return Array.from({ length: 5 }, (_, index) => ({
                __key: index,
                __formatedkey: index,
                _id: index,
                indexno: index + 1,
                period_detail: '',
                created_at: '',
                status: '',
                action: '',
            }));
        },

        /**
         * Reactively formats cycle report data for display in the data table
         * @returns {Array} Formatted array of cycle report items
         */
        formatedData() {
            return this.getCycleReportData.map((item, index) => ({
                __key: index,
                __formatedkey: index,
                _id: item._id,
                indexno: (this.currentPage - 1) * this.itemsPerPage + index + 1,
                serial_number: (this.currentPage - 1) * this.itemsPerPage + index + 1,
                user: item.user,
                period: item.period,
                type: item.type,
                errormessage: item.errormessage,
                errorstack: item.errorstack,
                stage: item.stage,
                status: this.getStages(item.stage),
                created_at: item.created_at,
                created_by: item.created_by,
                user_detail: item.user_detail,
                country_detail: item.country_details,
                region_detail: item.region_details,
                period_detail: item.period_detail,
                reportingunit_detail: item.reportingunit_details,
                network_detail: item.network_details,
                ru_type: item.ru_type,
            }));
        },
    },


    beforeDestroy() {
        if (this.fetchTimeout) clearTimeout(this.fetchTimeout);
        // Reset component data to prevent stale state
        this.selectedFilter = {};
        this.cycletype = 'Actual';
    },

    methods: {
        /**
         * Handles page change from Pagination component - fetches data for new page
         * @param {Number} page - New page number (1-based from Pagination component)
         */
        handlePageChange(page) {
            this.currentPage = page; // Update current page state for UI
            this.fetchPageData(page - 1); // API uses 0-based page index
        },

        /**
         * Handles status filter changes from DataTableNew column filter
         * Resets page to 1 and re-fetches with server-side status filtering
         * @param {Object} filters - Filter object from DataTableNew e.g. { status: ['ready', 'failed'] }
         */
        handleTableFilterChange(filters) {
            if (this.isInitialLoad) return; // Skip filter events during initial load
            this.statusFilter = filters.status || [];
            this.currentPage = 1;
            this.fetchPageData(0);
        },

        /**
         * Fetches data for a specific page
         * @param {Number} page - Page number to fetch
         */
        async fetchPageData(page) {
            try {
                await this.fetchCycleReportList({
                    page: page,
                    limit: this.itemsPerPage,
                    cycletype: this.cycletype,
                    type: 'allrucycle_' + this.cycletype.toLowerCase(),
                    ...(this.statusFilter.length ? { status: this.statusFilter } : {})
                });
            } catch (error) {
                console.error('[CycleReport]: Error fetching page data', error);
            }
        },
        getMonthName(monthNumber) {
            if (!monthNumber) return '';
            return MONTHS[monthNumber - 1] || monthNumber;
        },
        downloadFile(id) {
            this.$nova.downloadFile(undefined, {}, REPORTS_API.DOWNLOAD + id)
        },
        showDetails(item) {
            this.detailsDialog = true;
            this.detailsItem = item;
        },

        /**
         * Maps stage codes to their corresponding status variant constants
         * @param {Number} stage - The stage code (0=Generating, 1=Ready, 4=Generating, others=Failed)
         * @returns {String} The stage status variant constant
         */
        getStages(stage) {
            switch (stage) {
                case 0:
                    return STATUS_VARIANTS.GENERATING;
                case 1:
                    return STATUS_VARIANTS.READY;
                case 4:
                    return STATUS_VARIANTS.GENERATING;
                case 2:
                    return STATUS_VARIANTS.FAILED;
                default:
                    return STATUS_VARIANTS.FAILED;
            }
        },
        ...mapActions('varianceanalysis/reports', [
            'fetchCycleReportList',
            'fetchCycleReportFiltered',
        ]),

        /**
         * Handles filter data changes from GlobalFilter component
         * @param {Object} filterData - Filter data with key-value pairs
         */
        handleFilterData(filterData) {
            // Always update selectedFilter first with incoming data
            if (filterData.key && filterData.value !== undefined) {
                this.$set(this.selectedFilter, filterData.key, filterData.value);
            }

            // Handle initial filter load completion - fetch initial data once filters are ready
            if (filterData.key === 'loading' && filterData.value === false && this.isInitialLoad) {
                this.isInitialLoad = false;
                this.fetchInitialData();
                return;
            }

            // Only fetch filtered data when reload is triggered (Apply button clicked)
            if (filterData.reload) {
                if (this.fetchTimeout) clearTimeout(this.fetchTimeout);

                this.fetchTimeout = setTimeout(() => {
                    this.fetchFilteredData();
                }, 100);
            }
        },

        /**
         * Fetches initial paginated data on component mount
         * API: /v3/varianceanalysis/analysis/downloadallruexcellist/
         */
        async fetchInitialData() {
            try {
                this.currentPage = 1; // Reset to first page
                this.statusFilter = []; // Reset status filter
                await this.fetchCycleReportList({ page: 0, limit: this.itemsPerPage, cycletype: this.cycletype, type: 'allrucycle_' + this.cycletype.toLowerCase() });
            } catch (error) {
                console.error('[CycleReport]: Error fetching initial data', error);
            }
        },

        /**
         * Fetches filtered cycle report data
         * API: /v3/varianceanalysis/analysis/downloadallruexcel/
         */
        async fetchFilteredData() {
            try {
                this.filterLoading = true;
                this.currentPage = 1; // Reset to first page on filter apply

                const payload = {
                    period: [this.transformFilterValue(this.selectedFilter.selectedperiod)],
                    network: this.transformToArray(this.selectedFilter.selectednetwork),
                    country: this.transformToArray(this.selectedFilter.selectedcountry),
                    region: this.transformToArray(this.selectedFilter.selectedregion),
                    ruType: this.transformFilterValue(this.selectedFilter.selectedReportingUnitType),
                    reportingunit: this.transformReportingUnit(this.selectedFilter.selectedreportingunit),
                    type: 'allrucycle_' + this.cycletype.toLowerCase(),
                    cycletype: this.cycletype,
                };

                await this.fetchCycleReportFiltered(payload);
                await this.fetchInitialData();
            } catch (error) {
                console.error('[CycleReport]: Error fetching filtered data', error);
            } finally {
                this.filterLoading = false;
            }
        },

        /**
         * Transforms filter value: returns "all" if empty/null, otherwise returns the value
         * @param {String|null} value - Filter value
         * @returns {String} Transformed value
         */
        transformFilterValue(value) {
            return value || 'all';
        },

        /**
         * Transforms filter value to array: returns "all" if empty/null, otherwise returns array
         * Used for multi-select fields like network, region, country
         * @param {String|Array|null} value - Filter value
         * @returns {String|Array} Transformed value as array or 'all'
         */
        transformToArray(value) {
            if (!value || value === 'all' || (Array.isArray(value) && value.length === 0)) {
                return 'all';
            }
            return Array.isArray(value) ? value : [value];
        },

        /**
         * Transforms reporting unit value: returns "all" if empty or 'all' string, otherwise returns array
         * @param {String|Array|null} value - Reporting unit value
         * @returns {String|Array} Transformed value
         */
        transformReportingUnit(value) {
            if (!value || value === 'all' || (Array.isArray(value) && value.length === 0)) {
                return 'all';
            }
            return Array.isArray(value) ? value : [value];
        },
    },
};
</script>

<style scoped>
.failed-reason {
    color: var(--status-red);
}
</style>